import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../Common/Configurations/AuthContext";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import {
  RiArrowLeftLine,
  RiMoneyRupeeCircleLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiBusLine,
  RiUserLine,
  RiMapPinLine,
  RiShieldLine,
  RiTimeLine,
  RiInformationLine,
  RiAlertLine,
  RiLockLine,
  RiLockUnlockLine,
} from "react-icons/ri";
import "./BusPayment.css";

const BusPayment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    bus,
    searchQuery,
    selectedSeats,
    passengerCount,
    passengerDetails,
    selectedBoardingPoint,
    selectedDroppingPoint,
    totalAmount = 0,
    baseFare = 0,
    convenienceFee = 0,
  } = location.state || {};

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [bookingType, setBookingType] = useState("BOOK_DIRECTLY"); // 'BOOK_DIRECTLY' or 'BLOCK_ONLY'
  const [paymentMethod, setPaymentMethod] = useState("RAZORPAY");
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);
  const [blockTicketInfo, setBlockTicketInfo] = useState(null);
  const [markup, setMarkup] = useState(0);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (error) {
      return dateString;
    }
  };

  // Format time
  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Redirect if no booking data
  useEffect(() => {
    if (!bus || !selectedSeats || !passengerDetails) {
      navigate("/bus/results");
    }
  }, [bus, selectedSeats, passengerDetails, navigate]);
  useEffect(() => {
    if (user) {
      setMarkup(user.busMarkup || 0);
    }
  }, [user]);

  if (!bus || !selectedSeats || !passengerDetails) {
    return null;
  }

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return `INR${Math.round(num).toLocaleString("en-IN")}`;
  };

  const calculateBreakdown = () => {
    if (!selectedSeats) return { baseFare: 0, convenienceFee: 0, total: 0 };

    const base = selectedSeats.reduce(
      (sum, seat) => sum + (parseFloat(seat.totalPriceWithTaxes) || 0),
      0
    );
    const convenience = selectedSeats.reduce(
      (sum, seat) => sum + (parseFloat(seat.convenienceFee) || 100),
      0
    );
    const total = base + convenience;

    return {
      baseFare: base,
      convenienceFee: convenience,
      total,
    };
  };

  // Build common booking request data
  const buildBookingRequest = (isHold = false) => {
    const baseFare = selectedSeats.reduce(
      (sum, seat) => sum + (parseFloat(seat.totalPriceWithTaxes) || 0),
      0
    );

    const convenienceFee = selectedSeats.reduce(
      (sum, seat) => sum + (parseFloat(seat.convenienceFee) || 100),
      0
    );

    const totalFare = baseFare;
    const totalPrice = baseFare + convenienceFee;
    const gst = 18;
    const gstAmount = convenienceFee * (gst / 100);

    return {
      CompanyId: user?.cid || "00000000-0000-0000-0000-000000000000",
      BranchId: user?.branchId || "00000000-0000-0000-0000-000000000000",
      UserId: user?.id || "00000000-0000-0000-0000-000000000000",
      CustomerId: user?.customerId || "00000000-0000-0000-0000-000000000000",
      SupplierId: bus.supplierId,
      SourceCity: searchQuery.fromStation,
      DestinationCity: searchQuery.toStation,
      InventoryType: bus.inventoryType,
      RouteScheduleId: bus.routeScheduleId,
      BusType: bus.busType,
      OperatorName: bus.operatorName,
      JourneyType: "ONE_WAY",
      RequestedBy: "CUSTOMER",

      // Fare details
      BaseFare: baseFare,
      Tax: selectedSeats.reduce(
        (sum, seat) => sum + (parseFloat(seat.serviceTaxAmount) || 0),
        0
      ),
      ConvenienceFee: convenienceFee,
      GST: gst,
      GSTAmount: gstAmount,
      TotalFare: totalFare,
      TotalPrice: totalPrice,
      Markup: parseFloat(markup),

      // Customer information
      CustomerName: `${passengerDetails[0].firstName} ${passengerDetails[0].lastName}`,
      CustomerLastName: passengerDetails[0].lastName || "",
      CustomerEmail: passengerDetails[0].email,
      DOJ: new Date(searchQuery.travelDate),

      // Points
      BoardingPoint: {
        Id: selectedBoardingPoint.id,
        Location: selectedBoardingPoint.location,
        Time: selectedBoardingPoint.time,
      },
      DroppingPoint: {
        Id: selectedDroppingPoint.id,
        Location: selectedDroppingPoint.location,
        Time: selectedDroppingPoint.time,
      },

      CustomerPhone: passengerDetails[0].phone,
      EmergencyPhNumber: passengerDetails[0].phone,
      CustomerAddress: "Not provided",

      // Status for block vs book
      IsHold: isHold,
      BookingStatus: isHold ? "BLOCKED" : "BOOKED",
      PaymentStatus: isHold ? "PENDING" : "SUCCESS",

      // Payment information
      PaymentGatewayAmount: totalPrice,
      WalletAmount: 0,
      CreditAmount: 0,
      PaymentGateway: paymentMethod,
      CouponCode: "",

      // Passenger details
      BookSeatPaxDetails: selectedSeats.map((seat, index) => {
        const passenger = passengerDetails[index] || passengerDetails[0];
        const seatBaseFare = parseFloat(seat.totalPriceWithTaxes) || 0;
        const seatConvFee = parseFloat(seat.convenienceFee) || 100;

        return {
          Age: passenger.age,
          Name: `${passenger.firstName} ${passenger.lastName}`,
          SeatNbr: seat.id,
          Sex:
            passenger.title === "Ms" || passenger.title === "Mrs" ? "F" : "M",
          Fare: seatBaseFare,
          ServiceTaxAmount: seat.serviceTaxAmount || 0,
          OperatorServiceChargeAbsolute: seatConvFee,
          TotalFareWithTaxes: seatBaseFare,
          LadiesSeat: seat.ladiesSeat || false,
          LastName: passenger.lastName || "",
          Mobile: passenger.phone,
          Title: passenger.title,
          Email: passenger.email,
          IdType: passenger.idProof,
          IdNumber: passenger.idProofNumber || "",
          NameOnId: `${passenger.firstName} ${passenger.lastName}`,
          Primary: index === 0,
          Ac: seat.ac || false,
          Sleeper: seat.sleeper || false,
        };
      }),
    };
  };

  // Function to block seats only (no payment)
  const blockSeatsOnly = async () => {
    setIsProcessingPayment(true);
    try {
      const blockRequest = buildBookingRequest(true);

      const blockResponse = await apiService.post(
        config.BLOCKBUSSEATS,
        blockRequest
      );

      if (blockResponse && blockResponse.apiStatus?.success) {
        setBlockTicketInfo({
          blockTicketKey: blockResponse.bookTicketKey,
          bookingId: blockResponse.bookingId,
          bookingNo: blockResponse.bookingNo,
          invoiceNo: blockResponse.invoiceNo,
        });

        // Navigate to blocked confirmation
        navigateToConfirmation("BLOCKED", {
          bookingId: blockResponse.bookingId,
          bookingNo: blockResponse.bookingNo,
          invoiceNo: blockResponse.invoiceNo,
          bookTicketKey: blockResponse.bookTicketKey,
          message:
            "Seats blocked successfully! You can complete payment later.",
        });
      } else {
        throw new Error(
          blockResponse?.apiStatus?.message || "Failed to block seats"
        );
      }
    } catch (error) {
      console.error("Block seats error:", error);
      alert(`Failed to block seats: ${error.message}`);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Function to book directly (with payment)
  const bookDirectly = async () => {
    const res = await loadRazorpayScript();

    if (!res) {
      alert("Razorpay SDK failed to load. Are you online?");
      return;
    }

    try {
      const breakdown = calculateBreakdown();
      const finalAmount = breakdown.total * 100;

      // 1. Create payment order
      const paymentResult = await apiService.post(
        `${config.MAKEPAYMENT}?Amount=${breakdown.total}&PayRequest=${paymentMethod}&CompanyId=${user?.cid}`
      );

      if (!paymentResult.succeeded) {
        alert("Payment initiation failed. Please try again.");
        return;
      }

      const { id: order_id, currency, razorpayKey, name } = paymentResult.data;

      const options = {
        key: razorpayKey,
        amount: finalAmount,
        currency: currency,
        name: name,
        description: `Bus Booking - ${bus.operatorName} | ${searchQuery.fromStation} to ${searchQuery.toStation}`,
        order_id: order_id,
        handler: async function (paymentResponse) {
          try {
            // 2. Book seats after payment success
            const bookRequest = buildBookingRequest(false);
            bookRequest.PaymentReference = {
              paymentId: paymentResponse.razorpay_payment_id,
              orderId: paymentResponse.razorpay_order_id,
              signature: paymentResponse.razorpay_signature,
            };

            const bookResponse = await apiService.post(
              config.BOOKBUSSEATS,
              bookRequest
            );

            if (bookResponse && bookResponse.apiStatus?.success) {
              navigateToConfirmation("CONFIRMED", {
                bookingId: bookResponse.bookingId,
                bookingNo: bookResponse.bookingNo,
                invoiceNo: bookResponse.invoiceNo,
                supplierBookingId: bookResponse.supplierBookingId,
                pnr: bookResponse.pnr,
                bookTicketKey: bookResponse.bookTicketKey,
                saleInvoiceNumber: bookResponse.saleInvoiceNumber,
                purchaseInvoiceNumber: bookResponse.purchaseInvoiceNumber,
                paymentId: paymentResponse.razorpay_payment_id,
                orderId: paymentResponse.razorpay_order_id,
                message: "Booking confirmed successfully!",
              });
            } else {
              navigateToConfirmation("PAYMENT_SUCCESS_BOOKING_FAILED", {
                paymentId: paymentResponse.razorpay_payment_id,
                orderId: paymentResponse.razorpay_order_id,
                amount: breakdown.total,
                errorMessage: `Payment successful but booking failed: ${bookResponse?.apiStatus?.message}`,
              });
            }
          } catch (error) {
            console.error("Booking error:", error);
            navigateToConfirmation("PAYMENT_SUCCESS_BOOKING_PENDING", {
              paymentId: paymentResponse.razorpay_payment_id,
              orderId: paymentResponse.razorpay_order_id,
              amount: breakdown.total,
              message:
                "Payment successful! Please contact support for booking confirmation.",
            });
          }
        },
        prefill: {
          email: user?.email || passengerDetails[0]?.email,
          contact: user?.phoneNumber || passengerDetails[0]?.phone,
          name: `${passengerDetails[0]?.firstName} ${passengerDetails[0]?.lastName}`,
        },
        notes: {
          bookingType: "BUS_DIRECT_BOOKING",
          fromStation: searchQuery.fromStation,
          toStation: searchQuery.toStation,
          travelDate: formatDate(searchQuery.travelDate),
          passengerCount: passengerCount,
          seats: selectedSeats.map((s) => s.id).join(","),
        },
        theme: {
          color: "#3399cc",
        },
      };

      const paymentObject = new window.Razorpay(options);

      paymentObject.on("payment.failed", function (response) {
        console.error("Payment failed:", response.error);
        setIsProcessingPayment(false);
        navigateToConfirmation("PAYMENT_FAILED", {
          errorMessage: `Payment failed: ${response.error.description}`,
          errorCode: response.error.code,
        });
      });

      paymentObject.on("payment.cancel", function (response) {
        console.log("Payment cancelled by user:", response);
        setIsProcessingPayment(false);
        navigateToConfirmation("PAYMENT_CANCELLED", {
          message: "Payment was cancelled.",
        });
      });

      paymentObject.open();
    } catch (error) {
      console.error("Payment error:", error);
      alert("Payment initiation failed. Please try again.");
      setIsProcessingPayment(false);
    }
  };

  // Load Razorpay script
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Navigate to confirmation screen
  const navigateToConfirmation = (status, bookingData = {}) => {
    const breakdown = calculateBreakdown();

    const confirmationData = {
      bus,
      searchQuery,
      selectedSeats,
      passengerCount,
      passengerDetails,
      selectedBoardingPoint,
      selectedDroppingPoint,
      totalAmount: breakdown.total,
      baseFare: breakdown.baseFare,
      convenienceFee: breakdown.convenienceFee,
      bookingStatus: status,
      bookingType: bookingType,
      ...bookingData,
    };

    navigate("/bus/booking-confirmation", {
      state: confirmationData,
    });
  };

  // Handle booking based on selected type
  const handleBooking = async () => {
    setIsProcessingPayment(true);

    try {
      if (bookingType === "BLOCK_ONLY") {
        await blockSeatsOnly();
      } else {
        await bookDirectly();
      }
    } catch (error) {
      console.error("Booking error:", error);
      alert("An error occurred. Please try again.");
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleBackToSeats = () => {
    navigate(-1);
  };

  const breakdown = calculateBreakdown();

  return (
    <div id="layout-wrapper">
      <Header />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* Page Header */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">Complete Your Booking</h3>
                    <p className="text-muted mb-0">
                      Review details and proceed to payment
                    </p>
                  </div>
                  <button
                    className="btn btn-outline-secondary bp-back-btn"
                    onClick={handleBackToSeats}
                  >
                    <RiArrowLeftLine className="me-1" />
                    Back to Seats
                  </button>
                </div>
              </div>
            </div>

            <div className="row">
              {/* Left Column - Booking Details */}
              <div className="col-lg-8">
                {/* Booking Type Selection */}
                <div className="card mb-4 bp-card">
                  <div className="card-header bg-light bp-card-header">
                    <h5 className="mb-0">
                      <RiInformationLine className="me-2" />
                      Select Booking Type
                    </h5>
                  </div>
                  <div className="card-body bp-card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div
                          className={`bp-booking-type-card ${bookingType === "BOOK_DIRECTLY" ? "bp-selected" : ""
                            }`}
                          onClick={() => setBookingType("BOOK_DIRECTLY")}
                        >
                          <div className="bp-booking-type-icon">
                            <RiCheckboxCircleLine />
                          </div>
                          <div className="bp-booking-type-content">
                            <h6>Book Instantly</h6>
                            <p className="small bp-text-muted mb-0">
                              Pay now and get confirmed tickets immediately.
                              Recommended for instant confirmation.
                            </p>
                            <div className="bp-booking-type-badge">
                              <span className="badge bg-primary bp-badge bp-bg-primary">
                                Instant
                              </span>
                            </div>
                          </div>
                          {bookingType === "BOOK_DIRECTLY" && (
                            <div className="bp-booking-type-selected">
                              <RiCheckboxCircleLine className="bp-text-primary" />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <div
                          className={`bp-booking-type-card ${bookingType === "BLOCK_ONLY" ? "bp-selected" : ""
                            }`}
                          onClick={() => setBookingType("BLOCK_ONLY")}
                        >
                          <div className="bp-booking-type-icon bp-warning">
                            <RiCheckboxCircleLine />
                          </div>
                          <div className="bp-booking-type-content">
                            <h6>Block Seats Only</h6>
                            <p className="small bp-text-muted mb-0">
                              Reserve seats without payment. Pay later to
                              confirm booking.
                            </p>
                            <div className="bp-booking-type-badge">
                              <span className="badge bg-warning bp-badge bp-bg-warning">
                                Pay Later
                              </span>
                            </div>
                          </div>
                          {bookingType === "BLOCK_ONLY" && (
                            <div className="bp-booking-type-selected bp-warning">
                              <RiCheckboxCircleLine className="bp-text-warning" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Booking Type Info */}
                    <div
                      className={`bp-alert mt-3 ${bookingType === "BLOCK_ONLY"
                          ? "bp-alert-warning"
                          : "bp-alert-info"
                        }`}
                    >
                      {bookingType === "BLOCK_ONLY" ? (
                        <>
                          <strong>Note:</strong> Complete payment within this
                          time to confirm your booking. No payment required now.
                        </>
                      ) : (
                        <>
                          <RiCheckboxCircleLine className="me-2" />
                          <strong>Note:</strong> Your tickets will be confirmed
                          immediately after successful payment. You'll receive
                          e-ticket via email.
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Journey Details */}
                <div className="card mb-4 bp-card">
                  <div className="card-header bg-light bp-card-header">
                    <h5 className="mb-0">
                      <RiBusLine className="me-2" />
                      Journey Details
                    </h5>
                  </div>
                  <div className="card-body bp-card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="bp-journey-detail">
                          <span className="bp-detail-label">Route</span>
                          <h6 className="bp-detail-value">
                            {searchQuery.fromStation} → {searchQuery.toStation}
                          </h6>
                        </div>
                        <div className="bp-journey-detail">
                          <span className="bp-detail-label">Travel Date</span>
                          <p className="bp-detail-value">
                            {formatDate(searchQuery.travelDate)}
                          </p>
                        </div>
                        <div className="bp-journey-detail">
                          <span className="bp-detail-label">Operator</span>
                          <p className="bp-detail-value">{bus.operatorName}</p>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bp-journey-detail">
                          <span className="bp-detail-label">Departure</span>
                          <p className="bp-detail-value">
                            {formatTime(bus.departureTime)}
                          </p>
                        </div>
                        <div className="bp-journey-detail">
                          <span className="bp-detail-label">Arrival</span>
                          <p className="bp-detail-value">
                            {formatTime(bus.arrivalTime)}
                          </p>
                        </div>
                        <div className="bp-journey-detail">
                          <span className="bp-detail-label">Bus Type</span>
                          <p className="bp-detail-value">{bus.busType}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Passenger Details */}
                <div className="card mb-4 bp-card">
                  <div className="card-header bg-light bp-card-header">
                    <h5 className="mb-0">
                      <RiUserLine className="me-2" />
                      Passenger Details ({passengerCount})
                    </h5>
                  </div>
                  <div className="card-body bp-card-body">
                    <div className="bp-table-responsive">
                      <table className="table table-bordered bp-passenger-table">
                        <thead className="table-light">
                          <tr>
                            <th>Passenger</th>
                            <th>Seat</th>
                            <th>Age</th>
                            <th>ID Proof</th>
                            <th>Contact</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedSeats.map((seat, index) => {
                            const passenger =
                              passengerDetails[index] || passengerDetails[0];
                            return (
                              <tr key={seat.id}>
                                <td>
                                  <strong>
                                    {passenger.title} {passenger.firstName}{" "}
                                    {passenger.lastName}
                                  </strong>
                                </td>
                                <td>
                                  <span className="bp-badge bp-bg-primary">
                                    {seat.id}
                                  </span>
                                </td>
                                <td>{passenger.age} years</td>
                                <td>{passenger.idProof}</td>
                                <td>
                                  <div>{passenger.phone}</div>
                                  <small className="bp-text-muted">
                                    {passenger.email}
                                  </small>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Boarding Points */}
                <div className="card bp-card">
                  <div className="card-header bg-light bp-card-header">
                    <h5 className="mb-0">
                      <RiMapPinLine className="me-2" />
                      Boarding & Dropping Points
                    </h5>
                  </div>
                  <div className="card-body bp-card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="bp-boarding-point">
                          <h6 className="bp-text-primary">Boarding Point</h6>
                          <p className="mb-1">
                            <strong>{selectedBoardingPoint?.location}</strong>
                          </p>
                          <p className="bp-text-muted mb-1">
                            <RiTimeLine className="me-1" />
                            {formatTime(selectedBoardingPoint?.time)}
                          </p>
                          <small className="bp-text-muted">
                            {selectedBoardingPoint?.pointName ||
                              selectedBoardingPoint?.location}
                          </small>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="bp-boarding-point">
                          <h6 className="bp-text-success">Dropping Point</h6>
                          <p className="mb-1">
                            <strong>{selectedDroppingPoint?.location}</strong>
                          </p>
                          <p className="bp-text-muted mb-1">
                            <RiTimeLine className="me-1" />
                            {formatTime(selectedDroppingPoint?.time)}
                          </p>
                          <small className="bp-text-muted">
                            {selectedDroppingPoint?.pointName ||
                              selectedDroppingPoint?.location}
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Payment Summary */}
              <div className="col-lg-4">
                <div className="bp-sticky-sidebar" style={{ top: "20px" }}>
                  {/* Payment Summary Card */}
                  <div className="card border-primary bp-card bp-border-primary mb-4">
                    <div className="card-header bg-primary text-white bp-card-header bp-bg-primary">
                      <h5 className="mb-0">Payment Summary</h5>
                    </div>
                    <div className="card-body bp-card-body">
                      {/* Fare Breakdown */}
                      <div className="bp-fare-breakdown mb-3">
                        <div className="bp-fare-item">
                          <div className="bp-fare-label">
                            <span>Bus Fare</span>
                          </div>
                          <div className="bp-fare-amount">
                            {formatCurrency(breakdown.baseFare)}
                          </div>
                        </div>

                        <div className="bp-fare-item">
                          <div className="bp-fare-label">
                            <span>Convenience Fee</span>
                            <small className="bp-text-muted d-block">
                              INR100 per seat × {passengerCount}
                            </small>
                          </div>
                          <div className="bp-fare-amount">
                            {formatCurrency(breakdown.convenienceFee)}
                          </div>
                        </div>

                        <div className="bp-fare-divider"></div>

                        <div className="bp-fare-item bp-total">
                          <div className="bp-fare-label">
                            <strong>Total Amount</strong>
                          </div>
                          <div className="bp-fare-amount">
                            <strong className="bp-text-primary fs-5">
                              {formatCurrency(breakdown.total)}
                            </strong>
                          </div>
                        </div>
                         <hr></hr>
                        <div className="mb-3 px-1">
                          <div className="d-flex align-items-center justify-content-between">
                            <label className="medium fw-semibold text-muted mb-0">Add Markup</label>
                            <div style={{ width: '150px' }}>
                              <div className="input-group input-group-mb">
                                <span className="input-group-text">INR</span>
                                <input
                                  type="number"
                                  className="form-control text-end"
                                  value={markup}
                                  onChange={(e) => setMarkup(e.target.value >= 0 ? parseFloat(e.target.value) : 0)}
                                  min="0"
                                  placeholder="0"
                                />
                              </div>
                            </div>
                          </div>
                        

                        </div>
                      </div>

                      {/* Payment Method Selection */}
                      {bookingType === "BOOK_DIRECTLY" && (
                        <div className="mb-3">
                          <button
                            className="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center bp-payment-method-btn"
                            onClick={() =>
                              setShowPaymentMethods(!showPaymentMethods)
                            }
                          >
                            <span>
                              <RiShieldLine className="me-2" />
                              Payment Method
                            </span>
                            <span>{paymentMethod}</span>
                          </button>

                          {showPaymentMethods && (
                            <div className="bp-payment-methods-dropdown">
                              <div className="bp-form-check mb-2">
                                <input
                                  className="bp-form-check-input"
                                  type="radio"
                                  name="paymentMethod"
                                  id="razorpay"
                                  value="RAZORPAY"
                                  checked={paymentMethod === "RAZORPAY"}
                                  onChange={(e) =>
                                    setPaymentMethod(e.target.value)
                                  }
                                />
                                <label
                                  className="bp-form-check-label"
                                  htmlFor="razorpay"
                                >
                                  Razorpay (Credit/Debit Card, UPI, Net Banking)
                                </label>
                              </div>
                              <div className="bp-form-check">
                                <input
                                  className="bp-form-check-input"
                                  type="radio"
                                  name="paymentMethod"
                                  id="wallet"
                                  value="WALLET"
                                  checked={paymentMethod === "WALLET"}
                                  onChange={(e) =>
                                    setPaymentMethod(e.target.value)
                                  }
                                />
                                <label
                                  className="bp-form-check-label"
                                  htmlFor="wallet"
                                >
                                  Wallet Balance
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Button */}
                      <button
                        className={`bp-action-btn w-100 ${bookingType === "BLOCK_ONLY"
                            ? "bp-warning"
                            : "bp-success"
                          }`}
                        onClick={handleBooking}
                        disabled={isProcessingPayment}
                      >
                        {isProcessingPayment ? (
                          <>
                            <span className="bp-spinner"></span>
                            Processing...
                          </>
                        ) : bookingType === "BLOCK_ONLY" ? (
                          <>
                            <RiLockLine className="me-2" />
                            Block Seats
                          </>
                        ) : (
                          <>
                            <RiLockUnlockLine className="me-2" />
                            Pay {formatCurrency(breakdown.total)}
                          </>
                        )}
                      </button>

                      {/* Additional Info */}
                      <div className="bp-additional-info">
                        <div className="bp-secure-payment">
                          <RiShieldLine className="me-1" />
                          Secure & Encrypted Payment
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusPayment;
